import { useMutation } from '@apollo/client';
import type { DeleteObligationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  DeleteObligationDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteObligationTRPC } from './useDeleteObligationTRPC';

export const useDeleteObligation = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteObligationGraphQL, graphqlState] = useMutation(
    DeleteObligationDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation');
        evictField(cache, 'obligation_impact');
      },
      refetchQueries: [
        namedOperations.Query.getObligationById,
        namedOperations.Query.getObligationsByType,
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteObligationTRPC();

  const deleteObligation = async (
    id: string
  ): Promise<DeleteObligationMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteObligation(id);
    }

    const result = await deleteObligationGraphQL({
      variables: { id },
    });
    if (!result.data) {
      throw new Error('Failed to delete obligation');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteObligation,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteObligation,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
